import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import {
  FilterByChannel_Channel,
  useFilterByChannelFragment,
} from '~publish/components/FilterByChannel'
import type { FilterByChannelFragment } from '~publish/components/FilterByChannel'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { isService } from '~publish/helpers/channels'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'
import useMatchCurrentOrParentPath from '~publish/hooks/useMatchCurrentOrParentPath'
import { usePostTypeFilter } from '~publish/hooks/usePostTypeFilter'
import type { PostTypeFilterState } from '~publish/hooks/usePostTypeFilter'
import { useTagsQuery } from '~publish/hooks/useTags'
import type { Tag } from '~publish/hooks/useTags'
import { newCalendarSingleChannel } from '~publish/legacy/routes'
import { useMultiSelectFilter } from '~publish/pages/Calendar/hooks/useMultiSelectFilter'
import type { MultiSelectFilterState } from '~publish/pages/Calendar/hooks/useMultiSelectFilter'
import { getDisabledChannelIds } from '~publish/utils/permissions/getDisabledChannelIds'
import { getViewOnlyPublishChannelIds } from '~publish/utils/permissions/viewOnlyPublish'

export const GetAllChannelsInfoForCalendar = graphql(
  /* GraphQL */ `
    query GetAllChannelsInfoForCalendar {
      # TODO: channels should be a top-level query
      account {
        currentOrganization {
          channels(input: { filter: { isLocked: false } }) {
            id
            name
            service
            isQueuePaused
            timezone
            products
            allowedActions
            postingSchedule {
              day
              times
              paused
            }
            ...FilterByChannel_Channel
          }
          onboarding {
            responses {
              stepSlug
              selectedOptions
              skipped
            }
          }
        }
      }
    }
  `,
  [FilterByChannel_Channel],
)

type Channel = NonNullable<
  NonNullable<
    NonNullable<
      ResultOf<typeof GetAllChannelsInfoForCalendar>['account']
    >['currentOrganization']
  >['channels'][number]
>

export type CalendarFilters = {
  error: ErrorLike | undefined
  channelsLoading: boolean
  tagsFilter: MultiSelectFilterState<Tag>
  channels: Channel[]
  channelsFilter: MultiSelectFilterState<FilterByChannelFragment>
  /** Channels the actor can't view in Publish — greyed out in the filter. */
  disabledChannelIds: Set<string>
  /** Channels the actor can view but not post to — eye icon in the filter. */
  viewOnlyChannelIds: Set<string>
  onboardingChannelFocus: Service[] | null
  isSingleChannel: boolean
  /**
   * Post format filter. Only offered when exactly one channel is in scope,
   * because the formats depend on that channel's service.
   */
  postTypesFilter: PostTypeFilterState
}

export const useCalendarFilters = (): CalendarFilters => {
  const channelId = useParams<{ id?: string }>().id
  const isSingleChannel = useMatchCurrentOrParentPath(
    newCalendarSingleChannel.route,
  )

  const organizationId = useOrganizationId()
  const {
    data,
    error,
    loading: channelsLoading,
  } = useQuery(GetAllChannelsInfoForCalendar, {
    skip: !organizationId,
  })
  const channels = sanitizeNullableArray(
    data?.account?.currentOrganization?.channels,
  )
  const onboardingChannelFocus =
    data?.account?.currentOrganization?.onboarding?.responses
      .find(
        (response) =>
          response.stepSlug === 'channels-in-focus' && !response.skipped,
      )
      ?.selectedOptions.filter(isService) ?? null
  const channelFilterFragments = useFilterByChannelFragment(channels)
  const channelsFilter = useMultiSelectFilter(
    'channels',
    channelFilterFragments,
  )
  const disabledChannelIds = useMemo(
    () => getDisabledChannelIds(channels),
    [channels],
  )
  const viewOnlyChannelIds = useMemo(
    () => getViewOnlyPublishChannelIds(channels),
    [channels],
  )
  const mergedChannelsFilter = useMemo(() => {
    if (!isSingleChannel || channelId === undefined) {
      // The URL selection is user-editable state (saved views, bookmarks),
      // so scope it to channels the actor can view in Publish before it
      // reaches the posts query and the filter UI. An empty channel list
      // means "still loading" — pass through rather than treating every
      // selection as inaccessible. A selection pruned to nothing behaves
      // like no filter: the calendar falls back to all publish-visible
      // channels (the backend scopes post reads the same way).
      if (channels.length === 0) return channelsFilter
      const isAccessible = (id: string): boolean =>
        !disabledChannelIds.has(id) &&
        channels.some((channel) => channel.id === id)
      return {
        ...channelsFilter,
        sanitized: channelsFilter.sanitized.filter(isAccessible),
        queryParams: channelsFilter.queryParams?.filter(isAccessible),
        selected: channelsFilter.selected.filter((channel) =>
          isAccessible(channel.id),
        ),
        selectedIds: channelsFilter.selectedIds.filter(isAccessible),
      }
    }
    return {
      ...channelsFilter,
      sanitized: [channelId],
      queryParams: [channelId],
      selected: channelsFilter.selected.filter(
        (channel) => channel.id === channelId,
      ),
    }
  }, [channelsFilter, isSingleChannel, channelId, channels, disabledChannelIds])

  const { tags } = useTagsQuery()
  const tagsFilter = useMultiSelectFilter('tags', tags)

  // The formats on offer depend on the channel's service, so the control only
  // appears when one channel is in scope. On All Channels a mixed selection has
  // no single answer, so the filter stays off rather than guessing a union.
  const scopedChannelId =
    mergedChannelsFilter.sanitized.length === 1
      ? mergedChannelsFilter.sanitized[0]
      : undefined
  const scopedService = channels.find(
    (channel) => channel.id === scopedChannelId,
  )?.service
  const postTypesFilter = usePostTypeFilter(scopedService)

  return useMemo(
    () => ({
      error,
      channelsLoading,
      tagsFilter,
      channels,
      channelsFilter: mergedChannelsFilter,
      disabledChannelIds,
      viewOnlyChannelIds,
      onboardingChannelFocus,
      isSingleChannel,
      postTypesFilter,
    }),
    [
      tagsFilter,
      channels,
      mergedChannelsFilter,
      disabledChannelIds,
      viewOnlyChannelIds,
      onboardingChannelFocus,
      error,
      channelsLoading,
      isSingleChannel,
      postTypesFilter,
    ],
  )
}
